import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { localText } from '@/lib/localText';
import HeroSection from '@/components/HeroSection';
import ScrollReveal from '@/components/ScrollReveal';
import DocumentCenter from '@/components/DocumentCenter';
import { Shield, Building2, TrendingUp, ArrowRight, CheckCircle2, Globe2, Leaf } from 'lucide-react';

const Home = () => {
  const { lang, t } = useLanguage();
  const lt = (en: string, ar: string, fr?: string) => localText(lang, en, ar, fr);

  const section2Cols = [
    { icon: Shield, title: t.home.section2Col1Title, text: t.home.section2Col1Text },
    { icon: Building2, title: t.home.section2Col2Title, text: t.home.section2Col2Text },
    { icon: TrendingUp, title: t.home.section2Col3Title, text: t.home.section2Col3Text },
  ];

  const section3Steps = [
    { step: '01', title: t.home.section3Step1Title, desc: t.home.section3Step1Desc },
    { step: '02', title: t.home.section3Step2Title, desc: t.home.section3Step2Desc },
    { step: '03', title: t.home.section3Step3Title, desc: t.home.section3Step3Desc },
    { step: '04', title: t.home.section3Step4Title, desc: t.home.section3Step4Desc },
  ];

  return (
    <>
      <HeroSection />

      {/* SECTION 2 – INSTITUTIONAL POSITIONING */}
      <section className="py-32 bg-ivory relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container">
          <ScrollReveal className="text-center mb-24">
            <span className="text-secondary text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
              {lt('Institutional Governance', 'الحوكمة المؤسسية', 'Gouvernance Institutionnelle')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display mb-6">{t.home.section2Title}</h2>
            <div className="section-divider mt-8" />
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {section2Cols.map((col, i) => (
              <ScrollReveal key={i} delay={i * 0.12}>
                <div className="group text-center p-10 border border-border/50 hover:border-secondary/30 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_hsl(41_62%_44%/0.08)]">
                  <div className="inline-flex items-center justify-center w-16 h-16 border border-secondary/20 mb-8 group-hover:border-secondary/50 transition-colors duration-500">
                    <col.icon className="h-7 w-7 text-secondary" strokeWidth={1} />
                  </div>
                  <h3 className="font-display text-xl mb-4">{col.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{col.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 – STRATEGIC MODEL */}
      <section className="py-32 bg-charcoal text-primary-foreground relative">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container">
          <ScrollReveal className="text-center mb-24">
            <span className="text-secondary/70 text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
              {lt('Capital Allocation Protocol', 'بروتوكول تخصيص رأس المال', 'Protocole d\'Allocation de Capital')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display text-primary-foreground mb-6">{t.home.section3Title}</h2>
            <div className="section-divider mt-8" />
          </ScrollReveal>
          <div className="max-w-4xl mx-auto">
            {section3Steps.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="group flex items-start gap-10 py-12 border-b border-primary-foreground/8 last:border-0">
                  <span className="gold-gradient-text font-display text-4xl font-bold shrink-0 w-16">{item.step}</span>
                  <div>
                    <h3 className="font-display text-xl text-primary-foreground mb-3 group-hover:text-secondary transition-colors duration-300">{item.title}</h3>
                    <p className="text-primary-foreground/40 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 – GOVERNANCE STRUCTURE */}
      <section className="py-32 bg-ivory relative">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container">
          <ScrollReveal className="text-center mb-24">
            <span className="text-secondary text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
              {lt('Oversight', 'الرقابة', 'Supervision')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display mb-6">
              {t.home.section4Title}
            </h2>
            <div className="section-divider mt-8" />
          </ScrollReveal>

          <div className="max-w-lg mx-auto">
            {t.home.section4Roles.map((role: string, i: number) => (
              <ScrollReveal key={role} delay={i * 0.08}>
                <div className="text-center">
                  <div className="py-7 px-10 border border-border/60 font-medium tracking-wide hover:border-secondary/40 transition-all duration-300 bg-background">
                    {role}
                  </div>
                  {i < t.home.section4Roles.length - 1 && (
                    <div className="flex justify-center py-3">
                      <div className="w-px h-8 gold-gradient-bg opacity-40" />
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 – RISK ARCHITECTURE */}
      <section className="py-32 relative">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <ScrollReveal>
              <span className="text-secondary text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
                {lt('Risk Management', 'إدارة المخاطر', 'Gestion des Risques')}
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display mb-8 leading-tight">{t.home.section5Title}</h2>
              <p className="text-muted-foreground leading-relaxed text-lg">{t.home.section5Text}</p>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {t.home.section5Items.map((item: string, i: number) => (
                <ScrollReveal key={i} delay={i * 0.06}>
                  <div className="border border-border/50 p-7 hover:border-secondary/30 transition-all duration-500 hover:shadow-[0_10px_40px_-10px_hsl(41_62%_44%/0.06)] group">
                    <div className="w-1 h-6 gold-gradient-bg mb-4 opacity-40 group-hover:opacity-100 transition-opacity duration-500" />
                    <p className="font-medium">{item}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 – FUND STRUCTURE SNAPSHOT */}
      <section className="py-32 bg-charcoal text-primary-foreground relative">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container">
          <ScrollReveal className="text-center mb-24">
            <span className="text-secondary/70 text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
              {lt('Fund Overview', 'نظرة عامة على الصندوق', 'Aperçu du Fonds')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display text-primary-foreground mb-6">{t.home.section6Title}</h2>
            <div className="section-divider mt-8" />
          </ScrollReveal>
          <ScrollReveal>
            <div className="max-w-3xl mx-auto border border-primary-foreground/8 p-8 md:p-12">
              {t.home.section6Rows.map((row: string[], i: number) => (
                <div key={i} className={`flex justify-between py-5 ${i > 0 ? 'border-t border-primary-foreground/6' : ''}`}>
                  <span className="text-primary-foreground/40">{row[0]}</span>
                  <span className="text-right font-medium max-w-[55%] text-primary-foreground/80">{row[1]}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* SECTION 7 – GLOBAL FOOTPRINT */}
      <section className="py-32 bg-ivory relative">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container text-center">
          <ScrollReveal>
            <Globe2 className="h-16 w-16 text-secondary mx-auto mb-8" strokeWidth={0.8} />
            <span className="text-secondary text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
              {lt('Global Jurisdictional Presence', 'التواجد القضائي العالمي', 'Présence Juridictionnelle Mondiale')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display mb-4">
              {t.home.section7Title}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-16">
              {t.home.section7Subtitle}
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {t.home.section7Locations.map((loc: { flag: string; name: string; sub: string }, i: number) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="border border-border/50 p-12 hover:border-secondary/30 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_hsl(41_62%_44%/0.08)] group">
                  <span className="text-6xl block mb-6 group-hover:scale-110 transition-transform duration-500">{loc.flag}</span>
                  <h3 className="font-display text-lg mb-2">{loc.name}</h3>
                  <p className="text-muted-foreground text-sm">{loc.sub}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={0.3} className="text-center mt-16">
            <Link
              to={`/${lang}/mining-portfolio`}
              className="inline-flex items-center gap-3 px-10 py-4 border border-secondary/30 text-secondary text-sm font-semibold tracking-[0.1em] uppercase hover:bg-secondary/5 transition-all duration-300"
            >
              {lt('View Full Portfolio', 'عرض المحفظة الكاملة', 'Voir le Portefeuille Complet')} <ArrowRight className="h-4 w-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* SECTION 8 – ESG / SHARI'A / SUSTAINABILITY */}
      <section className="py-32 relative">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-start">
            <ScrollReveal>
              <div className="border border-secondary/20 p-16 text-center hover:border-secondary/40 transition-all duration-500">
                <Leaf className="h-16 w-16 text-secondary mx-auto mb-6" strokeWidth={0.8} />
                <h3 className="font-display text-2xl mb-4">{t.home.section8ShariahTitle}</h3>
                <p className="text-muted-foreground">{t.home.section8ShariahRef}</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <span className="text-secondary text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
                {lt('Sustainability', 'الاستدامة', 'Durabilité')}
              </span>
              <h2 className="text-3xl md:text-4xl font-display mb-10">{t.home.section8EsgTitle}</h2>
              <ul className="space-y-6">
                {t.home.section8EsgItems.map((item: string) => (
                  <li key={item} className="flex items-start gap-4">
                    <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-1" strokeWidth={1.5} />
                    <span className="text-muted-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 9 – DOCUMENT CENTER */}
      <DocumentCenter />

      {/* SECTION 10 – INVESTOR ACCESS */}
      <section className="py-32 bg-charcoal text-primary-foreground relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px gold-gradient-bg opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-charcoal to-charcoal-light" />
        <div className="relative container">
          <ScrollReveal className="text-center max-w-2xl mx-auto">
            <span className="text-secondary/70 text-xs tracking-[0.3em] uppercase font-semibold block mb-4">
              {lt('Capital Allocation', 'تخصيص رأس المال', 'Allocation de Capital')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display text-primary-foreground mb-8">{t.home.section10Title}</h2>
            <p className="text-primary-foreground/40 leading-relaxed mb-14">
              {t.home.section10Text}
            </p>
            <Link
              to={`/${lang}/investor`}
              className="inline-flex items-center gap-3 px-12 py-5 gold-gradient-bg text-charcoal text-sm font-semibold tracking-[0.1em] uppercase hover:opacity-90 transition-all duration-300 premium-shadow"
            >
              {t.home.section10Cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
};

export default Home;
